import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';

const ServicesSection = ({ sectionRef, translations }) => {
  const t = translations;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  const services = [
    {
      title: t.service1Title,
      desc: t.service1Desc,
      emoji: '🎂',
      color: 'pink'
    },
    {
      title: t.service2Title,
      desc: t.service2Desc,
      emoji: '🧁',
      color: 'purple'
    },
    {
      title: t.service3Title,
      desc: t.service3Desc,
      emoji: '🍰',
      color: 'indigo'
    }
  ];

  // Generate array of image paths
  const imageArray = Array.from({ length: 25 }, (_, i) => {
    const imageNumber = i + 1;
    return {
      src: require(`../assets/pic${imageNumber}.jpeg`),
      alt: `Sweet creation ${imageNumber}`,
      id: imageNumber
    };
  });

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === imageArray.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, imageArray.length]);

  const goToNext = () => {
    setCurrentIndex(currentIndex === imageArray.length - 1 ? 0 : currentIndex + 1);
  };

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? imageArray.length - 1 : currentIndex - 1);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
    setIsAutoPlaying(false);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setIsAutoPlaying(true);
  };

  // Calculate visible images for preview
  const getVisibleImages = () => {
    const visibleCount = 5;
    let visibleImages = [];
    
    for (let i = 0; i < visibleCount; i++) {
      const index = (currentIndex + i) % imageArray.length;
      visibleImages.push({
        ...imageArray[index],
        position: i
      });
    }
    
    return visibleImages;
  };

  return (
    <section ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            {t.servicesTitle}
          </h2>
          <p className="text-xl text-gray-600">{t.servicesSubtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {services.map((service, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br from-${service.color}-100 to-${service.color}-200 rounded-3xl p-8 text-center hover:shadow-xl transform hover:scale-105 transition-all duration-300`}
            >
              <div className="text-6xl mb-4">{service.emoji}</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
              <p className="text-gray-600">{service.desc}</p>
            </div>
          ))}
        </div>

        {/* Our Creations Carousel */}
        <div className="mt-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Our Sweet Creations</h3>
            <p className="text-lg text-gray-600">Discover our delicious masterpieces</p>
          </div>

          {/* Main Carousel Container */}
          <div className="relative">
            {/* Main Featured Image */}
            <div className="relative mb-8">
              <div className="aspect-[4/3] max-w-2xl mx-auto relative overflow-hidden rounded-3xl shadow-2xl group cursor-pointer">
                <img 
                  src={imageArray[currentIndex].src}
                  alt={imageArray[currentIndex].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  onClick={() => openImageModal(imageArray[currentIndex])}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-6 left-6 right-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <h4 className="text-xl font-bold mb-2">{imageArray[currentIndex].alt}</h4>
                  <p className="text-sm">Click to view in full size</p>
                </div>
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <ChevronRight className="w-6 h-6 text-gray-700" />
              </button>

              {/* Auto-play Toggle */}
              <button
                onClick={toggleAutoPlay}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200"
              >
                {isAutoPlaying ? <Pause className="w-5 h-5 text-gray-700" /> : <Play className="w-5 h-5 text-gray-700" />}
              </button>
            </div>

            {/* Thumbnail Preview Row */}
            <div className="flex justify-center space-x-3 mb-8 overflow-hidden">
              {getVisibleImages().map((image, index) => (
                <div
                  key={image.id}
                  className={`relative cursor-pointer transition-all duration-300 ${
                    index === 0 ? 'scale-110 ring-4 ring-pink-400' : 'scale-90 hover:scale-100'
                  }`}
                  onClick={() => goToSlide((currentIndex + index) % imageArray.length)}
                >
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden shadow-md">
                    <img 
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Dot Indicators */}
            <div className="flex justify-center space-x-2">
              {imageArray.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-pink-500 scale-125' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            {/* Image Counter */}
            <div className="text-center mt-6">
              <span className="text-gray-600 font-medium">
                {currentIndex + 1} / {imageArray.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-5xl max-h-full animate-fadeIn">
            <img 
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
            <button
              onClick={closeImageModal}
              className="absolute -top-4 -right-4 w-12 h-12 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center text-gray-800 hover:text-black transition-all duration-200 shadow-lg"
            >
              âœ•
            </button>
            <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 bg-white/95 px-6 py-3 rounded-full shadow-lg">
              <span className="text-gray-800 font-medium text-lg">{selectedImage.alt}</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ServicesSection;